import {
  Button,
  Callout,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeaderWithIcon,
} from '@dev-spendesk/grapes';
import React, { useReducer, useState } from 'react';

import { TrustDeviceModal } from 'src/auth/modules/StrongCustomerAuthentication/components/TrustDeviceModal/TrustDeviceModal';
import { type Token, TokenInput } from 'src/core/common/components/TokenInput';
import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { getAuthenticationOperationTranslations } from './getTranslations';
import {
  validateAuthenticationOperation,
  validateLoginAuthenticationOperation,
} from '../../api/validateScaProcedureWithTotp';

type FlowProps =
  | { flow: 'auth'; scaToken: string; operationId?: never }
  | { flow: 'standard'; scaToken?: never; operationId?: string };

type Props = {
  onBack: () => void;
  onTotpTokenValidated: () => Promise<void> | void;
} & FlowProps;

type TokenState = {
  token: Token;
  isTokenFilled: boolean;
};

const TOTP_TOKEN_LENGTH = 6;
const emptyToken: Token = new Array(TOTP_TOKEN_LENGTH).fill('');

function tokenReducer(tokenState: TokenState, action: Token): TokenState {
  const newToken = [...action, ...emptyToken].slice(0, TOTP_TOKEN_LENGTH);
  return {
    ...tokenState,
    token: newToken,
    isTokenFilled: newToken.every((char) => char !== ''),
  };
}

export const TotpModal = ({
  flow,
  onBack,
  onTotpTokenValidated,
  operationId,
  scaToken,
}: Props) => {
  const { t } = useTranslation('auth');

  const translations = getAuthenticationOperationTranslations('TOTP');

  const [{ token, isTokenFilled }, setToken] = useReducer(tokenReducer, {
    token: emptyToken,
    isTokenFilled: false,
  });

  const [isConfirmDeviceModalOpen, setIsConfirmDeviceModalOpen] =
    useState(false);

  const [verifyError, setVerifyError] = useState<
    'token-invalid' | 'token-expired' | undefined
  >(undefined);

  const handleValidateToken = async (
    validateTrustedDevice: boolean = false,
  ) => {
    const combinedToken = token.join('');
    if (flow === 'auth' && scaToken) {
      const result = await validateLoginAuthenticationOperation(
        combinedToken,
        scaToken,
        validateTrustedDevice,
      );

      if (result.outcome === 'failure') {
        setVerifyError(result.errorCode);
        return;
      }

      if (result.outcome === 'success') {
        await onTotpTokenValidated();
      }
    } else if (flow === 'standard' && operationId) {
      const result = await validateAuthenticationOperation({
        operationId,
        token: combinedToken,
      });
      if (result.outcome === 'failure') {
        setVerifyError(result.errorCode);
        return;
      }
      if (result.outcome === 'success') {
        await onTotpTokenValidated();
      }
    }
  };

  const handleSubmit = async () => {
    if (flow === 'auth') {
      setIsConfirmDeviceModalOpen(true);
    } else {
      await handleValidateToken(false);
    }
  };

  const handleConfirmDevice = async (validateTrustedDevice: boolean) => {
    await handleValidateToken(validateTrustedDevice);
    setIsConfirmDeviceModalOpen(false);
  };

  return (
    <>
      <ModalContent>
        <ModalHeaderWithIcon
          title={t(translations.title)}
          subtitle={t(translations.description)}
          iconName="lock-close"
          iconVariant="neutral"
          titleId="select-auth-factor-modal-title"
        />
        <ModalBody>
          <div className="flex w-full flex-col items-center justify-center px-40 py-32">
            <TokenInput
              token={token}
              tokenLength={TOTP_TOKEN_LENGTH}
              onTokenChange={setToken}
            />
          </div>
          {verifyError && (
            <Callout
              title={t(
                'sca.verifyAuthenticationOperationModal.errors.tokenInvalid',
              )}
              variant="alert"
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="secondaryNeutral"
            text={t('misc.cancel')}
            onClick={onBack}
          />
          <Button
            variant="primaryBrand"
            text={t('misc.continue')}
            isDisabled={!isTokenFilled}
            onClick={handleSubmit}
          />
        </ModalFooter>
      </ModalContent>
      <TrustDeviceModal
        isOpen={isConfirmDeviceModalOpen}
        onConfirmDevice={handleConfirmDevice}
      />
    </>
  );
};
